import './index.scss'
import { useCallback, useContext, useEffect, useRef, useState } from 'react'
import FlotiMap, { LAYER_TYPE } from '../../components/FlotiMap'
import LiveMap_Sidebar from './sidebar'
import UserContext from '../../contexts/UserContext/context'
import { initial_sidebar } from '../../contexts/SidebarContext/reducer'
import { useSidebar } from '../../hooks/useSidebar'
import { ICON } from '../../components/Icon/data'
import { FlotiMapRef } from '../../components/FlotiMap/type'
import { useTranslation } from 'react-i18next'
import Icon from '../../components/Icon'
import { COLOR, COOKIE } from '../../data/variables'
import { useCookies } from '../../hooks/useCookies'
import { getMobileProfile } from '../../services/calls/mobiles'
import MobileProfile from './sidebar/table/MobileDetailCol/MobileProfile'
import { useWindowSize } from '../../hooks/useWindowSize'
import Button from '../../components/Button'
import { useLiveMap } from './useLiveMap'

const LiveMap = () => {
  const { user } = useContext(UserContext)
  const sidebarRef = useRef()
  const mapRef = useRef<FlotiMapRef>()

  const [initialized, setInitialized] = useState<boolean>(false)
  const [initialFit, setInitialFit] = useState<boolean>(false)
  const [isMapBusy, setIsMapBusy] = useState<boolean>(true)

  const { updateSidebar, updateInfobar } = useSidebar()
  const { t } = useTranslation()
  const { size, isFullSize } = useWindowSize()

  const { getCookie, setCookie } = useCookies()

  const [fleets, setFleets] = useState<number[]>([])
  const { rawData, points, isLoading } = useLiveMap({ fleets, fetchData: initialized })

  useEffect(() => {
    let fleetLabel = t('Showing {{number}} fleets', { number: fleets.length })
    if (!fleets.length || fleets.length === user?.available_fleets.length) fleetLabel = t('Showing all fleets')
    else if (fleets.length === 1) {
      const fleetInfo = user?.available_fleets.find((f) => f.fleetid === Number(fleets[0]))
      fleetLabel = fleetInfo?.name ?? ''
    }

    mapRef?.current?.setInfoDisplay({
      label: (
        <>
          <Icon icon={ICON.CARS} />
        </>
      ),
      content: fleetLabel
    })
  }, [fleets])
  
  useEffect(() => {
    const storedFleetsCookie = getCookie(COOKIE.LIVEMAP_FLEET_SELECTION)
    const storedFleets = storedFleetsCookie ? storedFleetsCookie.split(',').map(Number) : undefined
    const defaultFleets = storedFleets ?? [user?.main_fleet ?? user?.available_fleets?.[0]?.fleetid]
    setFleets(defaultFleets)

    updateSidebar({
      content: (
        <LiveMap_Sidebar
          ref={sidebarRef}
          mapRef={mapRef}
          onFleetChange={(val) => {
            setFleets(val)
            setCookie(COOKIE.LIVEMAP_FLEET_SELECTION, val.toString())
          }}
          defaultFleets={defaultFleets}
        />
      ),
      isOpen: isFullSize,
      width: isFullSize ? 660 : size.width,
      header: isFullSize ? undefined : t('Live map data'),
    })

    setInitialized(true)
    return () => {
      updateSidebar(initial_sidebar)
    }
  }, [])

  useEffect(() => {
    if (sidebarRef?.current) sidebarRef?.current?.setIsLoading(isLoading)
  }, [isLoading, sidebarRef?.current])

  useEffect(() => {
    if (rawData && sidebarRef?.current) sidebarRef.current.setData(rawData)
  }, [rawData, sidebarRef?.current])

  useEffect(() => {
    if (points && mapRef?.current) {
      mapRef.current.setPoints(points)
    }
  }, [points, mapRef?.current])

  useEffect(() => {
    if (points?.length > 0 && mapRef?.current && !isMapBusy && !initialFit) {
      const didFit = mapRef.current.fitInMap(LAYER_TYPE.MARKERS)
      if (didFit) setInitialFit(true)
    }
  }, [points, mapRef?.current, isMapBusy, initialFit])

  const onMarkerClick = useCallback(async(mobileid: number) => {
    const details = await getMobileProfile(mobileid)
    if (details) {
      updateInfobar({
        isOpen: true,
        header: (
          <div className="Sidebar__header-label">
            {`${t('Car details for')}: `}<strong>{details.automobile.registration_number}</strong>
          </div>
        ),
        content: <MobileProfile data={details} />
      })
    }
  }, [updateInfobar, t])

  const openMobileSidebar = useCallback(() => {
    updateSidebar({ isOpen: true })
  }, [updateSidebar])

  const handleBusyChange = useCallback((busy: boolean) => {
    setIsMapBusy(busy)
  }, [])

  return (
    <div className="LiveMap">
      <div className="LiveMap__sidebar-button">
        <Button
          label={
            <>
              {t('Show data')}
              <Icon icon={ICON.LIST_BOX} color={COLOR.WHITE} />
            </>
          }
          size='s'
          onClick={openMobileSidebar}
        />
      </div>
      <FlotiMap
        ref={mapRef}
        onMarkerClick={onMarkerClick}
        options={{
          showClusterMarkers: true,
          showWeather: true,
          showRoadConditions: true,
          showLiveCams: true,
        }}
        isBusyCallback={handleBusyChange}
      />
    </div>
  )
}

export default LiveMap
